import { FC, useEffect, useRef } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Star } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  buildFavoriteTitle,
  isFavoriteTitleChanged,
  RenameFavoriteFormValues,
  renameFavoriteSchema,
} from "../lib/favoriteRename";

export type RenameFavoriteDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The favorite's current title, and the value the field opens on. */
  title: string;
  /** Whether the favorite points at a legislation source or a workspace document. */
  type: "legislation" | "workspaceDocument";
  /** Called with the trimmed new title. Only invoked when the title actually changed. */
  onRename: (title: string) => Promise<void>;
  isPending: boolean;
};

/**
 * Renames a favorite — the user's own label on it (LAR-418). The document behind the
 * favorite is untouched, which the description says out loud: a workspace document's own
 * name is renamed from the workspace, and a legislation source cannot be renamed at all.
 */
const RenameFavoriteDialog: FC<RenameFavoriteDialogProps> = ({
  isOpen,
  onClose,
  title,
  type,
  onRename,
  isPending,
}) => {
  const form = useForm<RenameFavoriteFormValues>({
    resolver: zodResolver(renameFavoriteSchema),
    defaultValues: { title },
  });

  // Re-seed the field whenever a different favorite is picked from the menu — the dialog
  // stays mounted, so the previous favorite's title would otherwise stick.
  useEffect(() => {
    if (isOpen) form.reset({ title });
  }, [isOpen, title, form]);

  const busy = isPending || form.formState.isSubmitting;
  const inFlight = useRef(false);

  const handleSubmit = form.handleSubmit(async (values) => {
    if (inFlight.current) return;

    if (!isFavoriteTitleChanged(title, values.title)) {
      onClose();
      return;
    }

    inFlight.current = true;
    try {
      await onRename(buildFavoriteTitle(values.title));
      onClose();
    } catch {
      // onRename reports the failure; keep the dialog open so the name is not lost.
    } finally {
      inFlight.current = false;
    }
  });

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
      isDirty={form.formState.isDirty}
      size="md"
    >
      <DialogHeader className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full">
            <Star className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-xl">Omdøb favorit</DialogTitle>
        </div>
        <DialogDescription className="text-sm text-muted-foreground">
          Giv favoritten et navn, der passer dig. Navnet vises kun i dine
          favoritter —{" "}
          {type === "legislation"
            ? "kilden beholder sin egen titel."
            : "dokumentet beholder sit navn i workspacet."}
        </DialogDescription>
      </DialogHeader>

      <Separator className="my-1" />

      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  <Pencil className="h-3.5 w-3.5 text-primary" />
                  Navn
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="F.eks. Miljøbekendtgørelsen"
                      className="pl-9"
                      autoFocus
                      data-testid="rename-favorite-title"
                    />
                  </FormControl>
                  <Star className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="gap-2 space-x-2 sm:gap-0 mt-6">
            <FormDialogCancel disabled={busy} />
            <Button
              type="submit"
              className="gap-2 min-w-[100px]"
              disabled={busy}
            >
              {busy ? (
                <Spinner className="h-4 w-4 text-primary-foreground" />
              ) : (
                <span className="flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  Gem
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default RenameFavoriteDialog;
